import { useRef, useState } from 'react'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { ImageIcon } from 'lucide-react'
import showErrorToast from '@/common/utils/showErrorToast'
import { useUploadImage } from '@/common/hooks/uploadImage'
import Cropper from 'react-easy-crop'
import type { Point, Area } from 'react-easy-crop'

interface UploadImageProps {
	onImageUploaded: (fileUrl: string) => void
	defaultValue?: string
	getFileUrl: (params: { payload: { fileExtension: string } }) => Promise<{
		presignedUrl: string
		fileUrl: string
	}>
	aspectRatio?: string // e.g. "16/9", "1/1", "500/250"
	className?: string
	previewHeight?: number // New prop for customizable preview height
}

// Function to create an image from a url
const createImage = (url: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const image = new Image()
		image.addEventListener('load', () => resolve(image))
		image.addEventListener('error', (error) => reject(error))
		image.src = url
	})

// Function to get the cropped image
async function getCroppedImg(
	imageSrc: string,
	pixelCrop: Area,
	rotation = 0,
): Promise<string> {
	const image = await createImage(imageSrc)
	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')

	if (!ctx) {
		throw new Error('No 2d context')
	}

	// Set canvas size to match the desired crop size
	canvas.width = pixelCrop.width
	canvas.height = pixelCrop.height

	// Draw rotated image
	ctx.translate(canvas.width / 2, canvas.height / 2)
	ctx.rotate((rotation * Math.PI) / 180)
	ctx.translate(-canvas.width / 2, -canvas.height / 2)

	// Draw the image with crop
	ctx.drawImage(
		image,
		pixelCrop.x,
		pixelCrop.y,
		pixelCrop.width,
		pixelCrop.height,
		0,
		0,
		pixelCrop.width,
		pixelCrop.height,
	)

	return canvas.toDataURL('image/webp')
}

export default function UploadImage({
	onImageUploaded,
	defaultValue,
	getFileUrl,
	aspectRatio = '16/9',
	className,
	previewHeight = 200, // Default preview height
}: UploadImageProps) {
	const fileInputRef = useRef<HTMLInputElement>(null)
	const { uploadImage, isUploading } = useUploadImage(getFileUrl)

	const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
	const [zoom, setZoom] = useState(1)
	const [rotation, setRotation] = useState(0)
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
	const [pendingImage, setPendingImage] = useState<string | null>(null)

	const [aspectWidth, aspectHeight] = aspectRatio.split('/').map(Number)
	const expectedRatio = aspectWidth / aspectHeight

	const handleGifUpload = async (file: File) => {
		// Load image to check dimensions
		const img = new Image()
		const objectUrl = URL.createObjectURL(file)

		img.onload = async () => {
			URL.revokeObjectURL(objectUrl)

			const imageRatio = img.width / img.height
			const tolerance = 0.01 // Allow small rounding differences

			// Check if aspect ratio matches
			if (Math.abs(imageRatio - expectedRatio) > tolerance) {
				showErrorToast(
					`GIF ต้องมีอัตราส่วน ${aspectRatio} (เช่น ${Math.round(1000 * expectedRatio)}x1000 หรือ ${Math.round(500 * expectedRatio)}x500) \nไฟล์ที่อัพโหลด: ${img.width}x${img.height}`,
				)
				return
			}

			// Dimensions match, upload directly
			try {
				await uploadImage(file, {
					onSuccess: (url) => {
						onImageUploaded(url)
					},
					onError: (error) => {
						console.error('Upload error:', error)
						showErrorToast('เกิดข้อผิดพลาดในการอัพโหลดรูปภาพ')
					},
				})
			} catch (error) {
				showErrorToast(error as Error)
			}
		}

		img.onerror = () => {
			URL.revokeObjectURL(objectUrl)
			showErrorToast('ไม่สามารถอ่านไฟล์ GIF ได้')
		}

		img.src = objectUrl
	}

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0]

			// Check if file is GIF
			if (file.type === 'image/gif') {
				await handleGifUpload(file)
			} else {
				const reader = new FileReader()
				reader.onload = () => {
					if (reader.result) {
						setPendingImage(reader.result as string)
					}
				}
				reader.readAsDataURL(file)
			}
		}
	}

	const handleUploadClick = () => {
		fileInputRef.current?.click()
	}

	const handleCropComplete = (_: Area, croppedAreaPixels: Area) => {
		setCroppedAreaPixels(croppedAreaPixels)
	}

	const handleConfirmCrop = async () => {
		if (!pendingImage || !croppedAreaPixels) return

		try {
			const croppedImage = await getCroppedImg(
				pendingImage,
				croppedAreaPixels,
				rotation,
			)

			// Convert base64 to blob and upload
			const response = await fetch(croppedImage)
			const blob = await response.blob()
			const file = new File([blob], 'cropped-image.webp', {
				type: 'image/webp',
			})

			await uploadImage(file, {
				onSuccess: (url) => {
					onImageUploaded(url)
					setPendingImage(null)
				},
				onError: (error) => {
					console.error('Upload error:', error)
					showErrorToast('เกิดข้อผิดพลาดในการอัพโหลดรูปภาพ')
				},
			})
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const previewWidth = (previewHeight * aspectWidth) / aspectHeight

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-4">
				<Button
					type="button"
					variant="outline"
					onClick={handleUploadClick}
					disabled={isUploading}
					className="w-full"
				>
					<ImageIcon className="mr-2 h-4 w-4" />
					{isUploading ? 'กำลังอัพโหลด...' : 'อัพโหลดรูปภาพ'}
				</Button>
			</div>

			<Input
				type="file"
				ref={fileInputRef}
				onChange={handleFileChange}
				className="hidden"
				accept="image/*"
			/>

			{pendingImage && (
				<div className="space-y-4">
					<div className="relative h-[400px] w-full overflow-hidden rounded-lg">
						<Cropper
							image={pendingImage}
							crop={crop}
							rotation={rotation}
							zoom={zoom}
							aspect={aspectWidth / aspectHeight}
							onRotationChange={setRotation}
							onCropChange={setCrop}
							onZoomChange={setZoom}
							onCropComplete={handleCropComplete}
						/>
					</div>
					<div className="flex gap-2">
						<Button
							type="button"
							onClick={handleConfirmCrop}
							disabled={isUploading}
						>
							{isUploading ? 'กำลังอัพโหลด...' : 'ยืนยันการครอป'}
						</Button>
						<Button variant="outline" onClick={() => setPendingImage(null)}>
							ยกเลิก
						</Button>
					</div>
				</div>
			)}

			{defaultValue && !pendingImage && (
				<div
					className={`relative mx-auto overflow-hidden rounded-lg ${className}`}
					style={{
						height: previewHeight,
						width: previewWidth,
						maxWidth: '100%',
					}}
				>
					<img
						src={defaultValue}
						alt="Preview"
						className="h-full w-full object-cover"
					/>
				</div>
			)}
		</div>
	)
}
